import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { FlatList, Platform, UIManager } from 'react-native';
import Animated, { FadeInUp, LayoutAnimationConfig } from 'react-native-reanimated';

import {
  CONTENT_CONTAINER_PADDING_MOBILE,
  ListCardGroupContainer,
  ListHeader,
  NUM_COLUMNS,
  RenderItemRowContainer,
} from './styled';
import { IListCardGroup, IRenderItemRow, ListCardGroupVariant } from './types';
import { formatData, groupItemsInRows } from './utils';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export const ListCardGroup: React.FC<IListCardGroup> = ({
  items,
  renderItem,
  header,
  centerHeader = false,
  ItemCustomizationComponent,
  selectedRow,
  numColumns = NUM_COLUMNS,
  variant = ListCardGroupVariant.GROUP,
  isFullContainer = false,
}) => {
  const isMountedRef = React.useRef<boolean>(false);

  // LayoutAnimationConfig skipEntering logic for web devices
  React.useLayoutEffect(() => {
    isMountedRef.current = true;
  });

  const renderItemRow = ({ item: rowItems, index: rowIndex }: IRenderItemRow): JSX.Element => {
    const isCustomizationVisible = selectedRow === rowIndex && !!ItemCustomizationComponent;

    return (
      <>
        <RenderItemRowContainer listCardGroupVariant={variant}>
          {rowItems.map((item: any, index: number) => {
            if (item.empty) {
              return <Box opacity={0} key={index} />;
            }
            return renderItem({ item, index, rowIndex });
          })}
        </RenderItemRowContainer>
        {isCustomizationVisible && (
          <Animated.View entering={FadeInUp}>{ItemCustomizationComponent}</Animated.View>
        )}
      </>
    );
  };

  const itemsInRows = React.useMemo(
    () => groupItemsInRows(formatData(items, numColumns), numColumns),
    [items, numColumns]
  );

  const listHeaderComponent = React.useMemo((): React.ReactElement | null => {
    if (!header) {
      return null;
    }

    if (typeof header === 'string') {
      return (
        <ListHeader
          style={{
            textTransform: 'capitalize',
            textAlign: centerHeader ? 'center' : undefined,
          }}
          listCardGroupVariant={variant}
        >
          {header}
        </ListHeader>
      );
    }

    return header;
  }, [centerHeader, header, variant]);

  if (!itemsInRows.length) {
    return null;
  }

  return (
    <LayoutAnimationConfig skipEntering>
      <ListCardGroupContainer $isFullContainer={isFullContainer}>
        <FlatList
          ListHeaderComponent={listHeaderComponent}
          contentContainerStyle={{ padding: CONTENT_CONTAINER_PADDING_MOBILE }}
          data={itemsInRows}
          renderItem={renderItemRow}
        />
      </ListCardGroupContainer>
    </LayoutAnimationConfig>
  );
};
